import { z } from "zod";

/*
 * Rich content shared by messages, posts, webhooks, bots and interactions:
 * embeds, interactive components, modals, command definitions, plus the
 * JSON shapes stored for presence, profiles and community configuration.
 * Every shape has a zod schema because most of them arrive from third parties.
 */

const httpUrl = z
  .string()
  .trim()
  .max(2048)
  .regex(/^https?:\/\/[^\s]+$/i, "Must be an http(s) URL");
/** Absolute http(s) URL or a same-origin media path. */
const mediaOrUrl = z
  .string()
  .trim()
  .max(2048)
  .regex(/^(https?:\/\/[^\s]+|\/media\/[0-9a-f-]{36})$/i, "Must be an http(s) URL");
const hex = z.string().regex(/^#[0-9a-fA-F]{6}$/);

/* ───────────── Embeds ───────────── */

export const embedSchema = z.object({
  /** rich (bots/webhooks), link (preview), gif, image, video */
  type: z.enum(["rich", "link", "gif", "image", "video"]).default("rich"),
  title: z.string().max(256).optional(),
  description: z.string().max(4096).optional(),
  url: httpUrl.optional(),
  color: hex.optional(),
  timestamp: z.string().datetime({ offset: true }).optional(),
  author: z.object({ name: z.string().min(1).max(256), url: httpUrl.optional(), iconUrl: mediaOrUrl.optional() }).optional(),
  footer: z.object({ text: z.string().min(1).max(2048), iconUrl: mediaOrUrl.optional() }).optional(),
  image: z.object({ url: mediaOrUrl, width: z.number().int().positive().optional(), height: z.number().int().positive().optional() }).optional(),
  thumbnail: z.object({ url: mediaOrUrl, width: z.number().int().positive().optional(), height: z.number().int().positive().optional() }).optional(),
  video: z.object({ url: mediaOrUrl, width: z.number().int().positive().optional(), height: z.number().int().positive().optional() }).optional(),
  provider: z.object({ name: z.string().max(256), url: httpUrl.optional() }).optional(),
  fields: z
    .array(z.object({ name: z.string().min(1).max(256), value: z.string().min(1).max(1024), inline: z.boolean().optional() }))
    .max(25)
    .optional(),
});
export type Embed = z.infer<typeof embedSchema>;
export const embedsSchema = z.array(embedSchema).max(10);

/* ───────────── Components (buttons, selects) ───────────── */

const customId = z.string().min(1).max(100);
const emoji = z.string().max(64).optional();

export const buttonSchema = z.object({
  type: z.literal("button"),
  style: z.enum(["primary", "secondary", "success", "danger", "link"]).default("secondary"),
  label: z.string().max(80).optional(),
  emoji,
  customId: customId.optional(),
  url: httpUrl.optional(),
  disabled: z.boolean().optional(),
});

export const selectSchema = z.object({
  type: z.literal("select"),
  /** string: fixed options; user / channel / role: pick from the community. */
  kind: z.enum(["string", "user", "channel", "role"]).default("string"),
  customId,
  placeholder: z.string().max(150).optional(),
  minValues: z.number().int().min(0).max(25).optional(),
  maxValues: z.number().int().min(1).max(25).optional(),
  disabled: z.boolean().optional(),
  options: z
    .array(z.object({ label: z.string().min(1).max(100), value: z.string().min(1).max(100), description: z.string().max(100).optional(), emoji, default: z.boolean().optional() }))
    .max(25)
    .default([]),
});

export const actionRowSchema = z.object({
  type: z.literal("row"),
  components: z.array(z.discriminatedUnion("type", [buttonSchema, selectSchema])).min(1).max(5),
});
export type ButtonComponent = z.infer<typeof buttonSchema>;
export type SelectComponent = z.infer<typeof selectSchema>;
export type MessageComponent = z.infer<typeof actionRowSchema>;
export const componentsSchema = z
  .array(actionRowSchema)
  .max(5)
  .superRefine((rows, ctx) => {
    for (const row of rows) {
      const selects = row.components.filter((c) => c.type === "select").length;
      if (selects > 0 && row.components.length > 1) ctx.addIssue({ code: "custom", message: "A select menu must be alone in its row" });
      for (const c of row.components) {
        if (c.type === "button" && c.style === "link" && !c.url) ctx.addIssue({ code: "custom", message: "Link buttons need a url" });
        if (c.type === "button" && c.style !== "link" && !c.customId) ctx.addIssue({ code: "custom", message: "Buttons need a customId" });
        if (c.type === "select" && c.kind === "string" && c.options.length === 0) ctx.addIssue({ code: "custom", message: "Select menus need options" });
      }
    }
  });

export const modalSchema = z.object({
  customId,
  title: z.string().min(1).max(45),
  fields: z
    .array(
      z.object({
        customId,
        label: z.string().min(1).max(45),
        style: z.enum(["short", "paragraph"]).default("short"),
        required: z.boolean().default(true),
        minLength: z.number().int().min(0).max(4000).optional(),
        maxLength: z.number().int().min(1).max(4000).optional(),
        placeholder: z.string().max(100).optional(),
        value: z.string().max(4000).optional(),
      }),
    )
    .min(1)
    .max(5),
});
export type Modal = z.infer<typeof modalSchema>;

/* ───────────── Slash commands ───────────── */

export const COMMAND_OPTION_TYPES = ["string", "integer", "number", "boolean", "user", "channel", "role"] as const;
export type CommandOptionType = (typeof COMMAND_OPTION_TYPES)[number];

export const commandOptionSchema = z.object({
  name: z
    .string()
    .min(1)
    .max(32)
    .regex(/^[a-z0-9_-]+$/, "Lowercase letters, numbers, dashes and underscores"),
  description: z.string().min(1).max(100),
  type: z.enum(COMMAND_OPTION_TYPES),
  required: z.boolean().default(false),
  choices: z
    .array(z.object({ name: z.string().min(1).max(100), value: z.union([z.string().max(100), z.number()]) }))
    .max(25)
    .optional(),
  minValue: z.number().optional(),
  maxValue: z.number().optional(),
  minLength: z.number().int().min(0).max(6000).optional(),
  maxLength: z.number().int().min(1).max(6000).optional(),
});
export type CommandOption = z.infer<typeof commandOptionSchema>;

export const commandSchema = z.object({
  name: z
    .string()
    .min(1)
    .max(32)
    .regex(/^[a-z0-9_-]+$/, "Lowercase letters, numbers, dashes and underscores"),
  description: z.string().min(1).max(100),
  category: z.string().trim().min(1).max(32).default("General"),
  options: z
    .array(commandOptionSchema)
    .max(25)
    .default([])
    .refine((opts) => {
      let seenOptional = false;
      for (const o of opts) {
        if (!o.required) seenOptional = true;
        else if (seenOptional) return false;
      }
      return true;
    }, "Required options must come before optional ones")
    .refine((opts) => new Set(opts.map((o) => o.name)).size === opts.length, "Option names must be unique"),
  requiredPermissions: z.number().int().min(0).default(0),
  cooldownSeconds: z.number().int().min(0).max(3600).default(0),
});
export type CommandDefinition = z.infer<typeof commandSchema>;

/** A command as offered to a user in the autocomplete. */
export interface CommandInfo {
  id: string;
  name: string;
  description: string;
  category: string;
  options: CommandOption[];
  cooldownSeconds: number;
  application: { id: string; name: string; iconUrl: string | null; native: boolean };
}

export type CommandArgValue = string | number | boolean;

/** Response a bot (or a native command) returns to an interaction. */
export const interactionResponseSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("message"),
    content: z.string().max(2000).default(""),
    embeds: embedsSchema.default([]),
    components: componentsSchema.default([]),
    ephemeral: z.boolean().default(false),
  }),
  /** Acknowledge now, follow up within 15 minutes using the interaction token. */
  z.object({ type: z.literal("defer"), ephemeral: z.boolean().default(false) }),
  /** Update the message the component is attached to. */
  z.object({
    type: z.literal("update"),
    content: z.string().max(2000).optional(),
    embeds: embedsSchema.optional(),
    components: componentsSchema.optional(),
  }),
  z.object({ type: z.literal("modal"), modal: modalSchema }),
]);
export type InteractionResponse = z.infer<typeof interactionResponseSchema>;

/* ───────────── Forwarding ───────────── */

export interface ForwardedFrom {
  messageId: string;
  source: "channel" | "dm";
  channelName: string | null;
  communityName: string | null;
  author: { id: string; username: string; displayName: string; avatarUrl: string | null };
  content: string;
  createdAt: string;
  /** Media are copied, so the forwarded message stays intact if the original is deleted. */
  mediaCount: number;
}

/* ───────────── Presence ───────────── */

export const PRESENCE_STATUSES = ["online", "idle", "dnd", "invisible"] as const;
export type PresenceStatusChoice = (typeof PRESENCE_STATUSES)[number];
/** What other people see. */
export type PresenceState = "online" | "idle" | "dnd" | "offline";

export const ACTIVITY_TYPES = ["playing", "listening", "watching", "working", "custom"] as const;
export type ActivityType = (typeof ACTIVITY_TYPES)[number];

export const customStatusSchema = z.object({
  text: z.string().trim().max(128).default(""),
  emoji: z.string().max(64).nullable().default(null),
  expiresAt: z.string().datetime({ offset: true }).nullable().default(null),
});
export type CustomStatus = z.infer<typeof customStatusSchema>;

export const activitySchema = z.object({
  type: z.enum(ACTIVITY_TYPES),
  name: z.string().trim().min(1).max(128),
  details: z.string().trim().max(128).optional(),
  startedAt: z.string().datetime({ offset: true }).optional(),
  expiresAt: z.string().datetime({ offset: true }).nullable().optional(),
});
export type Activity = z.infer<typeof activitySchema>;

export interface Presence {
  userId: string;
  status: PresenceState;
  customStatus: CustomStatus | null;
  activity: Activity | null;
  /** The community the user is active in, if they share it with the viewer. */
  activeCommunity: { id: string; name: string } | null;
  lastSeenAt: string | null;
  /** Kinds of devices currently connected. */
  devices: ("desktop" | "mobile" | "bot")[];
}

/* ───────────── Profiles & decorations ───────────── */

export const PROFILE_THEMES = ["default", "midnight", "dawn", "forest", "ocean", "ember", "lilac"] as const;
export const PROFILE_LAYOUTS = ["classic", "centered", "compact"] as const;
export const AVATAR_EFFECTS = ["none", "glow", "pulse", "sparkle", "orbit"] as const;

export interface DecorationInfo {
  key: string;
  name: string;
  description: string;
  /** free: everyone; achievement / event / staff: needs an unlock. */
  availability: "free" | "achievement" | "event" | "staff";
  animated: boolean;
  seasonal?: boolean;
}

/** Built-in avatar decorations. Rendered with CSS so they need no assets. */
export const DECORATIONS: DecorationInfo[] = [
  { key: "ring", name: "Simple ring", description: "A clean ring in your accent colour.", availability: "free", animated: false },
  { key: "dual", name: "Dual ring", description: "Two thin concentric rings.", availability: "free", animated: false },
  { key: "spin", name: "Spinning arc", description: "A gently rotating arc.", availability: "free", animated: true },
  { key: "aurora", name: "Aurora", description: "Shifting northern-lights gradient.", availability: "free", animated: true },
  { key: "pulse", name: "Heartbeat", description: "A soft pulsing halo.", availability: "free", animated: true },
  { key: "sparkles", name: "Sparkles", description: "Twinkling particles around your avatar.", availability: "achievement", animated: true },
  { key: "gold", name: "Gilded", description: "Earned by reaching 100 followers.", availability: "achievement", animated: true },
  { key: "snow", name: "First frost", description: "Seasonal winter decoration.", availability: "event", animated: true, seasonal: true },
  { key: "bloom", name: "Spring bloom", description: "Seasonal spring decoration.", availability: "event", animated: true, seasonal: true },
  { key: "staff", name: "Staff halo", description: "Reserved for platform staff.", availability: "staff", animated: true },
];

export const profileAppearanceSchema = z.object({
  accentColor: hex.nullable().optional(),
  theme: z.enum(PROFILE_THEMES).optional(),
  layout: z.enum(PROFILE_LAYOUTS).optional(),
  decoration: z.string().max(32).nullable().optional(),
  effect: z.enum(AVATAR_EFFECTS).optional(),
  featuredPostIds: z.array(z.string().uuid()).max(3).optional(),
  featuredCommunityIds: z.array(z.string().uuid()).max(6).optional(),
  links: z
    .array(z.object({ label: z.string().trim().min(1).max(40), url: httpUrl }))
    .max(5)
    .optional(),
  /** Show the badge row on the profile. */
  showBadges: z.boolean().optional(),
});
export type ProfileAppearance = z.infer<typeof profileAppearanceSchema>;

/* ───────────── Badges ───────────── */

export const BADGE_RARITIES = ["common", "uncommon", "rare", "epic", "legendary"] as const;
export type BadgeRarity = (typeof BADGE_RARITIES)[number];
export const BADGE_ICONS = ["star", "shield", "check", "code", "code_check", "bot", "bug", "house", "heart", "flag", "crown", "hammer", "sparkle", "calendar", "trophy", "fire", "leaf", "bolt"] as const;

/** Platform badges Atrium admins give by hand from the staff console. */
export const STAFF_GRANTED_BADGES = ["verified", "bug_hunter", "verified_bot_developer", "moderator_programme"] as const;
export type StaffGrantedBadge = (typeof STAFF_GRANTED_BADGES)[number];
export const staffBadgeSchema = z.object({ held: z.boolean() });

/**
 * Marks Atrium staff put on a community, shown next to its name. Only platform
 * admins can set them; the look of each lives in the web app (CommunityBadges).
 */
export const COMMUNITY_BADGES = ["official", "partner", "featured"] as const;
export type CommunityBadge = (typeof COMMUNITY_BADGES)[number];
export const communityBadgesSchema = z.object({ badges: z.array(z.enum(COMMUNITY_BADGES)).max(COMMUNITY_BADGES.length) });

export interface BadgeSummary {
  id: string;
  key: string;
  name: string;
  description: string;
  icon: string;
  iconUrl: string | null;
  color: string;
  rarity: BadgeRarity;
  category: string;
  communityId: string | null;
}

export interface UserBadge extends BadgeSummary {
  awardedAt: string;
  visible: boolean;
  position: number;
}

/* ───────────── Community configuration ───────────── */

export const onboardingSchema = z.object({
  enabled: z.boolean().default(false),
  welcomeTitle: z.string().trim().max(100).default(""),
  welcomeBody: z.string().trim().max(2000).default(""),
  requireRules: z.boolean().default(false),
  introChannelId: z.string().uuid().nullable().default(null),
  /**
   * Lobby: until a member finishes onboarding they can only see these channels
   * (plus nothing else). Empty = no lobby, members see everything straight away.
   */
  lobbyChannelIds: z.array(z.string().uuid()).max(10).default([]),
  questions: z
    .array(
      z.object({
        id: z.string().min(1).max(40),
        title: z.string().trim().min(1).max(100),
        multiple: z.boolean().default(false),
        required: z.boolean().default(false),
        options: z
          .array(
            z.object({
              id: z.string().min(1).max(40),
              label: z.string().trim().min(1).max(60),
              emoji: z.string().max(16).optional(),
              roleIds: z.array(z.string().uuid()).max(10).default([]),
            }),
          )
          .min(1)
          .max(20),
      }),
    )
    .max(10)
    .default([]),
});
export type OnboardingConfig = Partial<z.infer<typeof onboardingSchema>>;

export const automodSchema = z.object({
  /** Block repeated identical messages and message floods. */
  spamFilter: z.boolean().default(false),
  /** Maximum distinct mentions per message (0 = no limit). */
  maxMentions: z.number().int().min(0).max(50).default(0),
  /** Block invite links to other communities. */
  blockInvites: z.boolean().default(false),
  /** Block messages that are mostly capital letters. */
  capsFilter: z.boolean().default(false),
  /** Time out members who trip the filters repeatedly. */
  timeoutOnRepeat: z.boolean().default(false),
});
export type AutomodConfig = Partial<z.infer<typeof automodSchema>>;

/* ───────────── Platform events (outgoing webhooks & bots) ───────────── */

export const PLATFORM_EVENTS = [
  "message.created",
  "message.updated",
  "message.deleted",
  "member.joined",
  "member.left",
  "member.roles_updated",
  "post.created",
  "post.liked",
  "post.commented",
  "user.followed",
  "community.created",
  "webhook.updated",
] as const;
export type PlatformEvent = (typeof PLATFORM_EVENTS)[number];

export const PLATFORM_EVENT_INFO: Record<PlatformEvent, { label: string; scope: ("community" | "user" | "application")[] }> = {
  "message.created": { label: "Message created", scope: ["community", "application"] },
  "message.updated": { label: "Message updated", scope: ["community", "application"] },
  "message.deleted": { label: "Message deleted", scope: ["community", "application"] },
  "member.joined": { label: "Member joined", scope: ["community", "application"] },
  "member.left": { label: "Member left", scope: ["community", "application"] },
  "member.roles_updated": { label: "Member roles changed", scope: ["community", "application"] },
  "post.created": { label: "New post", scope: ["user", "community"] },
  "post.liked": { label: "Post liked", scope: ["user"] },
  "post.commented": { label: "Post commented on", scope: ["user"] },
  "user.followed": { label: "New follower", scope: ["user"] },
  "community.created": { label: "Community created", scope: ["user"] },
  "webhook.updated": { label: "Webhook configuration changed", scope: ["community", "user", "application"] },
};

/* OAuth2 scopes live in developer.ts with the rest of the developer platform contract. */
